import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useIsMobile } from '@/hooks/use-mobile';

interface IncomeChartProps {
  data: Record<string, number>;
}

const COLORS = [
  'hsl(195, 85%, 42%)',   /* secondary - turkos */
  'hsl(210, 25%, 30%)',   /* mörkt blå */
  'hsl(45, 100%, 50%)',   /* primary - gul */
  'hsl(185, 60%, 55%)',   /* accent - ljus turkos */
];

const formatNumber = (value: number) => value.toLocaleString('sv-SE');

const RADIAN = Math.PI / 180;

interface LabelProps {
  cx: number;
  cy: number;
  midAngle: number;
  outerRadius: number;
  name: string;
  percent: number;
  fill: string;
}

function renderOuterLabel({ cx, cy, midAngle, outerRadius, name, percent, fill }: LabelProps & { labelSize: number }) {
  const radius = outerRadius + 20;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill={fill}
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize={arguments[0].labelSize}
      fontWeight={600}
    >
      {`${name} ${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

export function IncomeChart({ data }: IncomeChartProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const chartData = [
    { name: t('postal.income.low'), value: data.income_low ?? 0 },
    { name: t('postal.income.lowMid'), value: data.income_low_mid ?? 0 },
    { name: t('postal.income.midHigh'), value: data.income_mid_high ?? 0 },
    { name: t('postal.income.high'), value: data.income_high ?? 0 },
  ].filter((d) => d.value > 0);

  const innerR = isMobile ? 40 : 55;
  const outerR = isMobile ? 70 : 90;
  const labelSize = isMobile ? 10 : 13;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm md:text-base">{t('postal.charts.incomeDistribution')}</CardTitle>
      </CardHeader>
      <CardContent className="px-2 md:px-6">
        <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={innerR}
              outerRadius={outerR}
              dataKey="value"
              label={(props) => renderOuterLabel({ ...props, fill: COLORS[props.index % COLORS.length], labelSize })}
              labelLine={false}
            >
              {chartData.map((_, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
              formatter={(value: number) => [formatNumber(value), t('postal.charts.persons')]}
            />
          </PieChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
